import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Formik, Form } from 'formik';
import { Grid, Typography, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import Card from 'components/Card';
import Loader from 'components/Loader';
import WarningMessage from 'components/WarningMessage';
import Select from 'components/Select';
import Input from 'components/Input';
import FileUploader from 'components/FileUploader';
import Button from 'components/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';

import { AttachFile } from '@material-ui/icons';

import { setImagePath, setImageUrl } from '../../store/actions';
import { loadConfigurations, upsertConfigurations, uploadFile } from '../../store/thunk';

import { availablePrints } from '../resources';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

export default function Receipt({ formikProps }) {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const location = useLocation();
  const isRegistration = location.pathname == '/cadastro-inicial/comprovantes';

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isLoading } = useSelector(state => state.operational);
  const { settingHistoryUserName, settingHistoryDateTime } = useSelector(state => state.operational);
  const { urlLogo, pathLogo, print, printNote } = useSelector(state => state.operational.receipt);

  const printNoteMaxLength = 255;

  useEffect(() => {
    if(establishmentId) {
      dispatch(loadConfigurations(userId, establishmentId));
    }
  }, [establishmentId]);

  const handleUploadLogo = e => {
    const file = e?.target?.files[0];
    const { type, size } = e?.target?.files[0];

    const supportTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const maxSize = 5242880;

    if(!supportTypes.includes(type)) {
      return toast.error('O formato da imagem não é suportado.');
    }

    if(size > maxSize) {
      return toast.error('O tamanho máximo da imagem não pode exceder 5 MB.');
    }

    dispatch(uploadFile(userId, establishmentId, { file }));
  }

  const handleSubmit = values => {
    const params = {
      pathLogo,
      print: values.print,
      printNote: values.printNote,
      page: 'Comprovantes'
    }

    dispatch(upsertConfigurations(userId, establishmentId, params));
  }

  return(
    <Card title="Comprovantes">
      <Grid xs={12} alignItems='center'>
        <Loader isLoading={isLoading}>
          <Formik
            enableReinitialize
            initialValues={{
              print,
              printNote
            }}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, setFieldValue }) => (
              <Form>
                <Grid xs={12}>
                  <Grid container>
                    {(!!settingHistoryUserName && !!settingHistoryDateTime) && (
                      <WarningMessage
                        message={`Última alteração feita por ${settingHistoryUserName} em ${moment(settingHistoryDateTime).format('LLL')}`}
                      />
                    )}
                    <Grid item xs={12}>
                      <Typography color="primary" style={{ borderBottom: '1px solid #E4E7EA' }}>
                        <b>Faça o upload do seu logotipo</b>
                      </Typography>
                      <Typography color="textSecondary" style={{ margin: '10px 0 10px 0' }}>
                        Imagem utilizada em recibos por email, PDF e orçamentos. Apenas nos formatos: PNG, JPEG e JPG.
                      </Typography>
                    </Grid>
                    <Grid item md={3} sm={4} xs={12}>
                      <div>
                        {!!urlLogo ? (
                          <Grid container>
                            <Grid item xs={12} style={{ marginTop: 10 }}>
                              <img src={urlLogo} width="72" alt="Logo" />
                            </Grid>
                          </Grid>
                        ) : (
                          <Grid container>
                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '200px', height: '165px', backgroundColor: '#F5F5F5', margin: '10px 0 10px 0' }}>
                              <FontAwesomeIcon
                                size="lg"
                                color="#1E5168"
                                icon={faUpload}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </div>
                      <FileUploader
                        fullWidth
                        name="pathLogo"
                        file={urlLogo}
                        onChange={handleUploadLogo}
                        customButtonTitle={isSm && (
                          <div style={{ display: 'flex', gap: 10 }}>
                            <AttachFile style={{ color: '#13BC4B' }} />
                            <Typography color='textSecondary' variant='body1'>
                              Carregar imagem
                            </Typography>
                          </div>
                        )}
                        customButtonStyle={{
                          width: '100%',
                          height: 40,
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #757575',
                          borderRadius: 5,
                          cursor: 'pointer'
                        }}
                      >
                        Escolher ficheiro
                      </FileUploader>
                    </Grid>
                  </Grid>
                  {!!urlLogo && (
                    <Grid container>
                      <Grid item xs={12} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                        <img src={urlLogo} width="72" alt="Logo" />
                        <IconButton
                          onClick={() => {
                            dispatch(setImagePath(''));
                            dispatch(setImageUrl(''));
                          }}
                        >
                          <Close />
                        </IconButton>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Grid container>
                  <Grid item xs={12} style={{ marginTop: 20 }}>
                    <Typography color="primary" style={{ borderBottom: '1px solid #E4E7EA' }}>
                      <b>Emissão de ticket</b>
                    </Typography>
                    <Typography color="textSecondary" style={{ margin: '10px 0 10px 0' }}>
                      Escolha como deseja emitir os tickets na entrada e saída dos veículos
                    </Typography>
                    <Select
                      name="print"
                      label="Selecione a emissão"
                      options={availablePrints}
                      value={values.print || formikProps?.values?.print}
                      onChange={e => {
                        setFieldValue('print', e.target.value);

                        if(formikProps) {
                          formikProps.setFieldValue('print', e.target.value);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} style={{ marginTop: 20 }}>
                    <Typography color="primary" style={{ borderBottom: '1px solid #E4E7EA' }}>
                      <b>Observação</b>
                    </Typography>
                    <Typography color="textSecondary" style={{ margin: '10px 0 10px 0' }}>
                      Escreva uma mensagem de observação fixa para as emissões (opcional)
                    </Typography>
                    <Input
                      name="printNote"
                      label="Observação na emissão"
                      placeholder="Escreva uma mensagem de observação fixa para as emissões (Opcional. Máx: 255 caracteres)"
                      style={{ marginLeft: 0 }}
                      maxLength={printNoteMaxLength}
                      value={values.printNote || formikProps?.values?.printNote}
                      onChange={e => {
                        handleChange(e);

                        if(formikProps) {
                          formikProps.handleChange(e);
                        }
                      }}
                    />
                  </Grid>
                  <Grid container xs={12} justify='flex-end'>
                    <Typography color="primary">
                      <b>Caracteres: {(values.printNote || '').length}/{printNoteMaxLength}</b>
                    </Typography>
                  </Grid>
                </Grid>
                {!isRegistration && (
                  <Grid container xs={12} justify='flex-end' style={{ marginTop: 10 }}>
                    <Button
                      fullWidth={isSm}
                      color="success"
                      type="submit"
                    >
                      Salvar
                    </Button>
                  </Grid>
                )}
              </Form>
            )}
          </Formik>
        </Loader>
      </Grid>
    </Card>
  );
}